/**
 * Knopf „Spalten · 7 von 16" im Kartenkopf und das Menü dazu (P3-Review Punkt c, vorschlag_v3 §5.3/5.8).
 * Gruppen in fester Reihenfolge (= Bedeutung), je Option Häkchen, Beschriftung und Erklärung aus TERMS;
 * Pflichtspalten angehakt und gesperrt. „Standard wiederherstellen" ist nur aktiv, wenn etwas abweicht.
 */
import { FC, useState } from 'react';
import { Box, Button, Checkbox, Divider, ListSubheader, Menu, MenuItem, Typography } from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import InfoTip from '../InfoTip';
import type { SpaltenMeta, Spaltenwahl } from './spaltenwahl';

interface Props {
  spalten: readonly SpaltenMeta[];
  wahl: Spaltenwahl;
}

const SpaltenMenue: FC<Props> = ({ spalten, wahl }) => {
  const [anker, setAnker] = useState<HTMLElement | null>(null);
  const gruppen = Array.from(new Set(spalten.map((s) => s.gruppe)));
  const sichtbar = new Set(wahl.sichtbar);
  // Kopplung `nurMit`: ohne sichtbaren Partner gesperrt, mit Grund (P2-V3 § 5: ausgegraut, „nur zusammen mit …").
  const gesperrtWeil = (s: SpaltenMeta): string | null => {
    if (!s.nurMit || sichtbar.has(s.nurMit)) return null;
    const partner = spalten.find((x) => x.key === s.nurMit);
    return `nur zusammen mit „${partner?.label ?? s.nurMit}“`;
  };
  return (
    <>
      <Button
        size="small"
        variant="outlined"
        color="inherit"
        startIcon={<ViewColumnIcon fontSize="small" />}
        onClick={(e) => setAnker(e.currentTarget)}
        aria-haspopup="menu"
        aria-label={`Spalten wählen (${wahl.sichtbar.length} von ${spalten.length} sichtbar${wahl.angepasst ? ', angepasst' : ''})`}
        title="Spalten wählen"
        // Nicht schrumpfen: sonst lief der Text bei 390 px über den Knopfrand (Abnahme 23.10.); schmal kürzer.
        sx={{ textTransform: 'none', whiteSpace: 'nowrap', borderColor: 'divider', flexShrink: 0 }}
      >
        {wahl.angepasst ? (
          <>
            {wahl.breite === 'schmal' ? 'Spalten' : 'Spalten · angepasst'}
            <Box component="span" aria-hidden sx={{ ml: 0.75, width: 7, height: 7, borderRadius: '50%', backgroundColor: '#4cceac' }} />
          </>
        ) : wahl.breite === 'schmal' ? (
          `${wahl.sichtbar.length}/${spalten.length}`
        ) : (
          `Spalten · ${wahl.sichtbar.length} von ${spalten.length}`
        )}
      </Button>
      <Menu anchorEl={anker} open={anker !== null} onClose={() => setAnker(null)} MenuListProps={{ dense: true }}>
        {gruppen.flatMap((g) => [
          <ListSubheader key={`g:${g}`} sx={{ lineHeight: '28px', fontWeight: 600, backgroundColor: 'background.paper' }}>
            {g}
          </ListSubheader>,
          ...spalten
            .filter((s) => s.gruppe === g)
            .map((s) => {
              const sperre = gesperrtWeil(s);
              return (
              <MenuItem
                key={s.key}
                role="menuitemcheckbox"
                aria-checked={sichtbar.has(s.key)}
                disabled={s.pflicht || sperre !== null}
                onClick={() => wahl.setze(s.key, !sichtbar.has(s.key))}
                sx={{ py: 0, '&.Mui-disabled': { opacity: sperre !== null ? 0.6 : 1 } }}
              >
                {/* Nur Anzeige: Zustand und Name trägt der Menüeintrag (menuitemcheckbox), nicht ein zweites Tab-Ziel. */}
                <Checkbox
                  size="small"
                  checked={sichtbar.has(s.key)}
                  disabled={s.pflicht || sperre !== null}
                  tabIndex={-1}
                  inputProps={{ 'aria-hidden': true }}
                  sx={{ p: 0.5, mr: 0.5 }}
                />
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {s.label}
                  {s.pflicht && (
                    <Box component="span" sx={{ ml: 0.75, fontSize: 11, color: 'text.secondary' }}>
                      immer sichtbar
                    </Box>
                  )}
                  {sperre && (
                    <Box component="span" sx={{ ml: 0.75, fontSize: 11, color: 'text.secondary' }}>
                      {sperre}
                    </Box>
                  )}
                </Typography>
                {s.tip && (
                  // Klicks auf das Info-Symbol sollen die Spalte nicht umschalten.
                  <Box component="span" onClick={(e) => e.stopPropagation()} sx={{ ml: 1, pointerEvents: 'auto' }}>
                    <InfoTip text={s.tip} />
                  </Box>
                )}
              </MenuItem>
              );
            }),
        ])}
        <Divider />
        <MenuItem
          disabled={!wahl.angepasst}
          onClick={() => {
            wahl.zuruecksetzen();
            setAnker(null);
          }}
        >
          <Typography variant="body2">Standard wiederherstellen</Typography>
        </MenuItem>
      </Menu>
    </>
  );
};

export default SpaltenMenue;
